package app.nestbridge.feature.profile.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreLoadingIndicator
import com.karawek.core.ui.components.CorePhoneTextField
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreTextField
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.profile.screen.CreateUserProfileEvent
import app.nestbridge.feature.profile.screen.CreateUserProfileScreen
import app.nestbridge.feature.profile.screen.CreateUserProfileState
import app.nestbridge.feature.profile.screen.VerificationStatus
import app.nestbridge.feature.profile.screen.canContinue
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import nestbridge.composeapp.generated.resources.Res
import nestbridge.composeapp.generated.resources.ic_nestbridge_logo
import org.jetbrains.compose.resources.painterResource
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.screen.AuthenticationEvent
import com.karawek.core.ui.components.CoreChip
import com.karawek.core.ui.components.CoreScaffold
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = CreateUserProfileScreen::class,
    scope = AppScope::class,
)
@Composable
fun CreateUserProfileUi(
    state: CreateUserProfileState,
    modifier: Modifier = Modifier,
) {
    val subtitle = buildString {
        append("Tell us a bit about yourself. This helps care teams know who they're connecting with.")

        val missingContacts = buildList {
            if (state.phoneStatus != VerificationStatus.Verified) add("a phone number")
            if (state.emailStatus != VerificationStatus.Verified) add("an email address")
        }

        if (missingContacts.isNotEmpty()) {
            append(" You can add ${missingContacts.joinToString(" and ")} to help match " +
                    "invitations and make it easier for care teams to find you.")
        }
    }

    CoreScaffold(
        title = "Complete your profile",
        subtitle = subtitle,
        footer = { Footer(state) },
        onAppear = { state.eventSink(CreateUserProfileEvent.OnAppear) },
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
            ) {
                CoreTextField(
                    value = state.fullName,
                    onValueChange = { state.eventSink(CreateUserProfileEvent.UpdateFullName(it)) },
                    label = "Your name",
                    placeholder = "e.g., Maria Garcia",
                    modifier = Modifier.fillMaxWidth(),
                    error = null,
                )

                ContactField(
                    label = "Phone number",
                    placeholder = "(555) 123-4567",
                    value = state.phoneNumber,
                    status = state.phoneStatus,
                    onValueChange = { state.eventSink(CreateUserProfileEvent.UpdatePhoneNumber(it)) },
                    onVerify = { state.eventSink(CreateUserProfileEvent.VerifyPhone) },
                )

                ContactField(
                    label = "Email address",
                    placeholder = "you@example.com",
                    value = state.emailAddress,
                    status = state.emailStatus,
                    onValueChange = { state.eventSink(CreateUserProfileEvent.UpdateEmailAddress(it)) },
                    onVerify = { state.eventSink(CreateUserProfileEvent.VerifyEmail) },
                )

                state.relayEmail?.let { relay ->
                    ContactField(
                        label = "Apple relay email",
                        placeholder = relay,
                        value = relay,
                        status = VerificationStatus.Verified,
                        onValueChange = {},
                        onVerify = null,
                    )
                }
            }

            state.errorMessage?.let {
                Text(
                    text = it,
                    style = CoreTypography.caption,
                    color = CoreTheme.colors.error,
                )
            }
        }
    }
}

@Composable
private fun Footer(state: CreateUserProfileState) =
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
    ) {
        CorePrimaryButton(
            text = if (state.isSaving) "Saving..." else "Continue",
            enabled = !state.isSaving && state.canContinue(),
            onClick = { state.eventSink(CreateUserProfileEvent.Continue) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        Text(
            text = "Your contact information is only shared with care teams you choose to join.",
            style = CoreTypography.footnote,
            color = CoreTheme.colors.textSecondary,
        )
    }

@Composable
private fun ContactField(
    label: String,
    placeholder: String,
    value: String,
    status: VerificationStatus,
    onValueChange: (String) -> Unit,
    onVerify: (() -> Unit)?,
) {
    val isPhoneField = label.contains("Phone", ignoreCase = true) || label.contains("phone", ignoreCase = true)
    val isEmailField = label.contains("Email", ignoreCase = true) || label.contains("email", ignoreCase = true)
    
    // Determine if the verify button should be enabled
    val isValidInput = if (isPhoneField) {
        com.karawek.core.util.PhoneNumberFormatter.isValid(value)
    } else if (isEmailField) {
        value.contains("@") && value.contains(".") && value.length > 3
    } else {
        false
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (isPhoneField) {
            CorePhoneTextField(
                value = value,
                onValueChange = onValueChange,
                label = label,
                placeholder = placeholder,
                modifier = Modifier.weight(1f),
                enabled = status == VerificationStatus.Unverified,
                error = null,
            )
        } else {
            CoreTextField(
                value = value,
                onValueChange = onValueChange,
                label = label,
                placeholder = placeholder,
                modifier = Modifier.weight(1f),
                enabled = status == VerificationStatus.Unverified,
                error = null,
            )
        }
        VerificationBadge(
            status = status,
            onVerify = onVerify,
            enabled = isValidInput,
        )
    }
}

@Composable
private fun VerificationBadge(
    status: VerificationStatus,
    onVerify: (() -> Unit)?,
    enabled: Boolean = true,
) {
    when (status) {
        VerificationStatus.Verified -> {
            CoreChip(
                leadingIcon = Icons.Filled.CheckCircle,
                label = "Verified",
                selected = false,
                enabled = false,
                shape = CoreShapes.xl,
                modifier = Modifier,
                onClick = {},
            )
        }

        VerificationStatus.Pending -> {
            CoreChip(
                label = "Verifying…",
                selected = false,
                enabled = false,
                shape = CoreShapes.xl,
                modifier = Modifier,
                onClick = {},
            )
        }

        VerificationStatus.Unverified -> {
            CoreChip(
                label = "Verify",
                selected = false,
                enabled = enabled,
                shape = CoreShapes.xl,
                modifier = Modifier,
                onClick = onVerify ?: {},
            )
        }
    }
}

// region Previews

@Preview
@Composable
private fun CreateUserProfileApplePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        CreateUserProfileUi(
            state = CreateUserProfileState(
                fullName = "Maria Garcia",
                phoneNumber = "",
                emailAddress = "",
                relayEmail = "user123@privaterelay.appleid.com",
            ),
        )
    }
}

@Preview
@Composable
private fun CreateUserProfileEmailVerifiedPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        CreateUserProfileUi(
            state = CreateUserProfileState(
                fullName = "Maria Garcia",
                phoneNumber = "(555) 123-4567",
                emailAddress = "user@gmail.com",
                emailStatus = VerificationStatus.Verified,
            ),
        )
    }
}

@Preview
@Composable
private fun CreateUserProfilePhoneVerifiedPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        CreateUserProfileUi(
            state = CreateUserProfileState(
                fullName = "Maria Garcia",
                phoneNumber = "312-555-1212",
                emailAddress = "you@example.com",
                phoneStatus = VerificationStatus.Verified,
            ),
        )
    }
}

// endregion
